


import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../hooks/storeHook";
import { ChatAction } from "../actions/chatAction";
import { ActionTypes } from "../store/actionTypes";
import { dispatchChatSendFailure } from "../utils/chatSendFailure";
import { useSearchParams, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Paper,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddTaskIcon from "@mui/icons-material/AddTask";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import ChatBubble from "../components/ChatBubble";
import { BEFORE_FIRST_MESSAGE } from "../utils/computerUseRunPlacement";
import RemoteSessionInputSection from "../components/RemoteSessionInputSection";
import type { ChatSendPayload } from "../components/InputBox";
// DISABLED: pills bar — re-enable when restoring PillsBar + pills service
// import PillsBar, { PillCategory } from "../components/PillsBar";
// import PromptsList from "../components/PromptsList";
import Typeahead from "../components/Typeahead";
// import { fetchPills } from "../store/action";
import { cleanContent, formatTimestamp } from "../utils/chatUtils";
import { collapseStreamingChunks, groupConsecutivePlanSteps } from "../shared/utils/messageUtils";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Divider from "@mui/material/Divider";
import { DeleteModal } from "../shared/components";
import { ProductivityAppsWarningBanner } from "../shared/components/ProductivityAppsWarningBanner";
import { integrationApiInstance } from "../apis/integrationApi";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import theoLogo from "../assets/img/theo_link.svg";
import SidebarIcon from "../assets/img/sidebar_left.svg";
import CircularProgress from "@mui/material/CircularProgress";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AddTaskOutlinedIcon from "@mui/icons-material/AddTaskOutlined";
import ControlPointDuplicateOutlinedIcon from "@mui/icons-material/ControlPointDuplicateOutlined";
import { openInNewTab } from "../utils/openInNewTab";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Tooltip from "@mui/material/Tooltip";
import PushPinIcon from "@mui/icons-material/PushPin";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
// Removed TransitionGroup/CSSTransition to eliminate findDOMNode warnings
// Using CSS transitions directly instead
import ThinkingIndicator from "../components/ThinkingIndicator";
import ToolAuditLogPopup from "../components/ToolAuditLogPopup";
import LiveDebugPanel from "../components/LiveDebugPanel";
import { useLiveDebug } from "../hooks/useLiveDebug";
import UndoActionsModal from "../components/UndoActionsModal";
import LangSmithThreadLinkButton from "../components/LangSmithThreadLinkButton";
import ClearChatButton from "../components/ticketToolbar/ClearChatButton";
import { NEUTRAL, RADIUS, VIOLET } from "../styles/brandTokens";
import ConnectionNotice from "../components/ticketToolbar/ConnectionNotice";
import { TOOLBAR_GROUP_SX, toolbarButtonSx, toolbarIconSize } from "../components/ticketToolbar/toolbarStyles";
import { AssistantMode, chatApiInstance, type LinearTaskAssignee } from "../apis/chatApi";
import {
  TypingIndicator,
  extractResearchJobId,
  ResearchReportJobStatusInline,
} from "../shared/components";
import { useUserMsp } from "../hooks/useUserMsp";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { canAccessAnalyticsAssistant } from "../utils/analyticsAssistantAccess";
import { useIntegration } from "../hooks/useIntegration";
import { Customer } from "../types/integration";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { withOverlaySafeMenuProps } from "../utils/overlayMenu";
import { getDropdownSafeDialogSx } from "../utils/dialogLayering";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import { isHorizonRootRole } from "../utils/horizonRoles";
import {
  parseReportAttachmentsFromContent,
  stripReportAttachmentTokensForDisplay,
  type ReportAttachmentTokenPayload,
} from "../shared/utils/reportAttachmentTokens";
import {
  agentDisplayNameForMessage,
  getChatBubblePresentation,
  isNeoAssistantAuthor,
} from "../shared/utils/messageSenderLabels";
import {
  canSendQueuedUserMessageNow,
  canSendQueuedUserMessage,
  hasStreamingAssistantMessage,
  shouldQueueNewUserMessage,
} from "../shared/utils/chatQueueUtils";
import { useSSE } from "../hooks/useSSE";
import useThreadHistory from "../hooks/useThreadHistory";
import useComputerUseChatDisabledReason from "../hooks/useComputerUseChatDisabledReason";
import useThreadWait, { composeChatDisabledReason, deleteThreadWarning } from "../hooks/useThreadWait";
import useChatReplyPlaceholder from "../hooks/useChatReplyPlaceholder";
import useComputerUseRuns from "../hooks/useComputerUseRuns";
import { getSSE } from "../shared/utils/sseSingleton";

/** Query params used to seed one report attachment into Chat Assistant composer. */
const OPEN_REPORT_JOB_ID_QUERY_PARAM = "open_report_job_id";
const OPEN_REPORT_TITLE_QUERY_PARAM = "open_report_title";
const OPEN_REPORT_COMPLETED_AT_QUERY_PARAM = "open_report_completed_at";

type UserChatSendOptions = {
  assistantMode?: AssistantMode;
  attachments?: ChatSendPayload["attachments"];
};

type QueuedUserChatMessage = {
  id: string;
  content: string;
  options?: UserChatSendOptions;
};

// --- Inline useUserChatHook ---
function useUserChatHook(
  selectedUserThreadId: string | null,
  userId: string,
  callbacks?: { onSendError?: (message: string) => void }
) {
  const dispatch = useAppDispatch();
  const onSendError = callbacks?.onSendError;
  const { userThreads, isLoading, error, isThinking } = useSelector(
    (state: any) => state.chat
  );
  const selectedThread = userThreads.find(
    (t: any) => String(t.ai_thread_id) === String(selectedUserThreadId)
  );
  const messages = selectedThread?.messages || [];
  const queuedMessagesRef = useRef<QueuedUserChatMessage[]>([]);
  const [queuedMessages, setQueuedMessages] = useState<QueuedUserChatMessage[]>([]);
  const sendInFlightRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const queueThreadIdRef = useRef<string | null>(null);

  const syncQueuedMessages = useCallback((
    updater: QueuedUserChatMessage[] | ((current: QueuedUserChatMessage[]) => QueuedUserChatMessage[])
  ) => {
    const next =
      typeof updater === "function"
        ? (updater as (current: QueuedUserChatMessage[]) => QueuedUserChatMessage[])(queuedMessagesRef.current)
        : updater;
    queuedMessagesRef.current = next;
    setQueuedMessages(next);
  }, []);

  useEffect(() => {
    const threadKey = selectedUserThreadId ? String(selectedUserThreadId) : null;
    if (queueThreadIdRef.current === threadKey) return;
    syncQueuedMessages([]);
    sendInFlightRef.current = false;
    stopRequestedRef.current = false;
    queueThreadIdRef.current = threadKey;
  }, [selectedUserThreadId, syncQueuedMessages]);

  const { isConnected, connectionStatus, error: sseError } = useSSE(selectedUserThreadId, userId);

  const sendMessageNow = useCallback(async (
    content: string,
    options?: UserChatSendOptions
  ) => {
    if (!selectedUserThreadId) return;

    sendInFlightRef.current = true;
    stopRequestedRef.current = false;
    getSSE(selectedUserThreadId)?.connect();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    dispatch({
      type: ActionTypes.ADD_MESSAGE,
      payload: {
        message_id: `temp_${requestId}`,
        ai_thread_id: selectedUserThreadId,
        user_id: userId,
        content:
          options?.attachments?.length
            ? `${content}${content.trim() ? "\n\n" : ""}${options.attachments
                .map((item) => `[Attachment: ${item.filename}]`)
                .join("\n")}`
            : content,
        content_type: "MESSAGE",
        created_at: { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
        pending: true,
        requestId,
      },
    });

    dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
    dispatch({ type: ActionTypes.START_THINKING });
    
    try {
      await dispatch(
        ChatAction.handleMessage(selectedUserThreadId, userId, content, undefined, requestId, {
          assistantMode: options?.assistantMode ?? AssistantMode.CHAT,
          attachments: options?.attachments,
        })
      );
    } catch (error) {
      console.error("[ChatAssistant] Failed to send message:", error);
      const message = dispatchChatSendFailure(dispatch, error, requestId);
      onSendError?.(message);
    } finally {
      sendInFlightRef.current = false;
    }
  }, [dispatch, onSendError, selectedUserThreadId, userId]);

  const drainQueuedMessage = useCallback(() => {
    if (sendInFlightRef.current) return;
    if (queuedMessagesRef.current.length === 0) return;
    if (
      !canSendQueuedUserMessage(messages, {
        isLoading,
        isThinking,
        allowAfterLastUserMessage: stopRequestedRef.current,
      })
    ) {
      return;
    }

    const next = queuedMessagesRef.current[0];
    if (!next) return;
    syncQueuedMessages(queuedMessagesRef.current.slice(1));
    void sendMessageNow(next.content, next.options);
  }, [isLoading, isThinking, messages, sendMessageNow, syncQueuedMessages]);

  useEffect(() => {
    drainQueuedMessage();
  }, [drainQueuedMessage]);

  const sendMessage = async (
    content: string,
    options?: UserChatSendOptions
  ) => {
    if (!selectedUserThreadId) return;

    if (
      sendInFlightRef.current ||
      shouldQueueNewUserMessage(messages, {
        isLoading,
        isThinking,
      })
    ) {
      syncQueuedMessages((current) => [
        ...current,
        {
          id: `queued_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          content,
          options,
        },
      ]);
      return;
    }

    await sendMessageNow(content, options);
  };

  const deleteQueuedMessage = useCallback((id: string) => {
    syncQueuedMessages((current) => current.filter((item) => item.id !== id));
  }, [syncQueuedMessages]);

  const requestStopTurn = useCallback(async () => {
    if (!selectedUserThreadId) return;
    stopRequestedRef.current = true;
    try {
      await dispatch(ChatAction.cancelTurn(selectedUserThreadId));
    } catch (error) {
      console.error("[ChatAssistant] Failed to stop turn:", error);
    }
  }, [dispatch, selectedUserThreadId]);

  const canSendQueuedMessagesNow = canSendQueuedUserMessageNow(messages, {
    isLoading,
    isThinking,
    canCancelActiveTurn: Boolean(selectedUserThreadId),
  });

  const sendQueuedMessageNow = useCallback((id: string) => {
    if (!selectedUserThreadId) return;
    const next = queuedMessagesRef.current.find((item) => item.id === id);
    if (!next) return;

    const activeTurn =
      sendInFlightRef.current ||
      isLoading ||
      isThinking ||
      hasStreamingAssistantMessage(messages);

    if (activeTurn) {
      syncQueuedMessages((current) => [
        next,
        ...current.filter((item) => item.id !== id),
      ]);
      void requestStopTurn();
      return;
    }

    if (
      !canSendQueuedUserMessageNow(messages, {
        isLoading,
        isThinking,
      })
    ) {
      return;
    }
    syncQueuedMessages((current) => current.filter((item) => item.id !== id));
    void sendMessageNow(next.content, next.options);
  }, [isLoading, isThinking, messages, requestStopTurn, selectedUserThreadId, sendMessageNow, syncQueuedMessages]);

  const updateQueuedMessage = useCallback((id: string, content: string) => {
    const trimmed = String(content || "").trim();
    syncQueuedMessages((current) =>
      current.flatMap((item) => {
        if (item.id !== id) return [item];
        const attachments = item.options?.attachments ?? [];
        const hasAttachments = attachments.length > 0;
        if (!trimmed && !hasAttachments) return [];
        return [{ ...item, content: trimmed }];
      })
    );
  }, [syncQueuedMessages]);

  const stopTurn = requestStopTurn;

  return {
    messages,
    aiThreadId: selectedUserThreadId,
    isLoading,
    error: error || sseError,
    sendMessage,
    stopTurn,
    queuedMessages: queuedMessages.map((item) => ({
      id: item.id,
      content: item.content,
      attachments: item.options?.attachments,
    })),
    deleteQueuedMessage,
    updateQueuedMessage,
    sendQueuedMessageNow,
    canSendQueuedMessagesNow,
    isConnected,
    connectionStatus,
  };
}
// --- End Inline useUserChatHook ---

const MIN_WIDTH_FOR_EXPANDED_SIDEBAR = 900; // px, adjust as needed
/** Dialog option value for chats with no customer; maps to null for createUserThread. */
const CHAT_NEW_THREAD_NO_CUSTOMER = "__no_customer__";

function resolveNewChatCustomerIdForApi(selected: string | null): string | null {
  if (selected == null || selected === "" || selected === CHAT_NEW_THREAD_NO_CUSTOMER) {
    return null;
  }
  return selected;
}

/** Title stored in ai_threads.thread_name (via rename API) for sidebar + header after reload. */
function getInitialPersistedChatThreadTitle(
  selectedCustomerId: string | null,
  customers: Customer[]
): string {
  if (!selectedCustomerId || selectedCustomerId === CHAT_NEW_THREAD_NO_CUSTOMER) {
    return "New Chat";
  }
  const customer = customers.find((c: Customer) => String(c.id) === selectedCustomerId);
  return customer ? `New Chat with ${customer.name}` : "New Chat";
}

function parseResearchEnabled(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return false;
  if (["1", "true", "yes", "y"].includes(text)) return true;
  if (["0", "false", "no", "n"].includes(text)) return false;
  return Boolean(value);
}

type ChatAssistantSurface = "chat" | "deepResearch";

interface ChatAssistantProps {
  surface?: ChatAssistantSurface;
}

function isResearchEnabledThread(thread: any): boolean {
  return parseResearchEnabled(thread?.research_enabled);
}

function protoStringValue(value: any): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object" && "value" in value) return protoStringValue(value.value);
  return String(value).trim();
}

function isDeviceAssistantThread(thread: any): boolean {
  const sourceType = protoStringValue(thread?.device_source_type);
  const sourceId = protoStringValue(thread?.device_source_id);
  const provider = protoStringValue(thread?.device_provider);
  const deviceId = protoStringValue(thread?.device_external_id);
  const threadName = protoStringValue(thread?.thread_name);
  return Boolean(sourceType || sourceId || provider || deviceId || /^Device\s*:/i.test(threadName));
}

const ChatAssistant: React.FC<ChatAssistantProps> = ({ surface = "chat" }) => {
  const isDeepResearchSurface = surface === "deepResearch";
  const user = useSelector((state: any) => state.auth.user);
  const { userMspInfo } = useUserMsp();
  const flags = useFeatureFlag();
  const canUseDeepResearch = canAccessAnalyticsAssistant(
    flags as Record<string, unknown>,
    userMspInfo?.role
  );
  const { threadId: routeThreadId } = useParams<{ threadId: string }>();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    customers,
    getCustomers,
    customersLoading,
    customersLoaded,
    customersError,
  } = useIntegration();
  const isRootUser = isHorizonRootRole(userMspInfo?.role);
  const canFileLinearTask = isRootUser;
  const canOpenLangSmith = isRootUser;
  const assistantBasePath = isDeepResearchSurface ? "/deep-research" : "/chat-assistant";
  const userThreadsRaw = useSelector((state: any) => state.chat.userThreads);
  const userThreads = React.useMemo(() => {
    const threads = Array.isArray(userThreadsRaw) ? userThreadsRaw : [];
    return threads.filter(
      (thread: any) =>
        !thread?.ticket_id?.value &&
        !isDeviceAssistantThread(thread) &&
        (!isDeepResearchSurface || isResearchEnabledThread(thread))
    );
  }, [isDeepResearchSurface, userThreadsRaw]);
  const selectedUserThreadId = useSelector(
    (state: any) => state.chat.selectedUserThreadId
  );
  // const pills = useSelector((state: any) => state.pills.pills) ?? [];
  // const pillsKeyword = useSelector((state: any) => state.pills.keyword);

  // Get thinking indicator state from Redux
  const { isThinking, thinkingSteps, currentThinkingStep } = useSelector(
    (state: any) => state.chat
  );

  const dispatch = useAppDispatch();

  // Local state for chat input (pills/prompts disabled — see commented blocks below)
  const [inputMessage, setInputMessage] = useState("");
  // const [selectedPill, setSelectedPill] = useState<string | null>(null);
  // const [showPrompts, setShowPrompts] = useState(false);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [typeaheadDebounceTimer, setTypeaheadDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [hasNavigatedToTypeahead, setHasNavigatedToTypeahead] = useState(false);
  const [debouncedInputText, setDebouncedInputText] = useState(''); // Add debounced input state
  const inputRef = useRef<HTMLInputElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [autoCollapsed, setAutoCollapsed] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);

  // Add state for menu/dialog
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuThread, setMenuThread] = useState<any>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [threadToRename, setThreadToRename] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<any>(null);
  const [auditLogPopupOpen, setAuditLogPopupOpen] = useState(false);
  const [liveDebug, setLiveDebug] = useLiveDebug();
  const [undoModalOpen, setUndoModalOpen] = useState(false);
  const [undoPreSelected, setUndoPreSelected] = useState<string[]>([]);
  const openUndoModalWithPreSelection = (ids: string[]) => {
    setUndoPreSelected(ids);
    setUndoModalOpen(true);
  };
  const [renameLoading, setRenameLoading] = useState(false);
  const [showFileLinearTaskDialog, setShowFileLinearTaskDialog] = useState(false);
  const [fileLinearTaskTitle, setFileLinearTaskTitle] = useState("");
  const [fileLinearTaskDescription, setFileLinearTaskDescription] = useState("");
  const [fileLinearTaskThreadLink, setFileLinearTaskThreadLink] = useState("");
  const [fileLinearTaskTraceUrl, setFileLinearTaskTraceUrl] = useState("");
  const [fileLinearTaskAssignee, setFileLinearTaskAssignee] = useState("");
  const [fileLinearTaskAssignees, setFileLinearTaskAssignees] = useState<LinearTaskAssignee[]>([]);
  const [fileLinearTaskAssigneesLoading, setFileLinearTaskAssigneesLoading] = useState(false);
  const [fileLinearTaskTraceLoading, setFileLinearTaskTraceLoading] = useState(false);
  const [fileLinearTaskSubmitting, setFileLinearTaskSubmitting] = useState(false);
  const [createdLinearTask, setCreatedLinearTask] = useState<{
    identifier: string;
    title: string;
    url: string;
  } | null>(null);

  // Snackbar state (unified)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [preloadedReportAttachments, setPreloadedReportAttachments] = useState<
    ReportAttachmentTokenPayload[]
  >([]);
  const [preloadForThreadId, setPreloadForThreadId] = useState<string | null>(null);
  const consumedAttachmentParamRef = useRef<Set<string>>(new Set());

  const showChatError = useCallback((message: string) => {
    setSnackbarMsg(message);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }, []);

  const normalizeThreadCustomerId = useCallback((value: unknown): number | null => {
    const raw =
      value && typeof value === "object" && "value" in (value as Record<string, unknown>)
        ? (value as Record<string, unknown>).value
        : value;
    if (raw === null || raw === undefined || raw === "") return null;
    const parsed = Number.parseInt(String(raw), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, []);

  const [notFoundThreadId, setNotFoundThreadId] = useState<string | null>(null);
  const [shareUpdating, setShareUpdating] = useState(false);
  // Chat history pagination
  const CHATS_PER_PAGE = 15;
  const [chatHistoryPage, setChatHistoryPage] = useState(0);
  const normalizeEmail = (value: any): string =>
    String(value || "").trim().toLowerCase();
  const currentUserEmail = normalizeEmail(user?.email);

  // Memoize sorted pinned threads to prevent expensive re-sorting on every render
  const sortedPinnedThreads = React.useMemo(() => {
    return [...userThreads]
      .filter(
        (thread: any) =>
          thread.pinned && normalizeEmail(thread.user_id) === currentUserEmail
      )
      .sort((a: any, b: any) => {
        const aTime = a.updated_at?.seconds || 0;
        const bTime = b.updated_at?.seconds || 0;
        return bTime - aTime;
      });
  }, [userThreads, currentUserEmail]);

  // Memoize sorted unpinned threads to prevent expensive re-sorting on every render
  const sortedUnpinnedThreads = React.useMemo(() => {
    return [...userThreads]
      .filter(
        (thread: any) =>
          !thread.pinned && normalizeEmail(thread.user_id) === currentUserEmail
      )
      .sort((a: any, b: any) => {
        const aTime = a.updated_at?.seconds || 0;
        const bTime = b.updated_at?.seconds || 0;
        return bTime - aTime;
      });
  }, [userThreads, currentUserEmail]);

  // Memoize paginated threads to prevent re-slicing on every render
  const paginatedUnpinnedThreads = React.useMemo(() => {
    const totalPages = Math.ceil(sortedUnpinnedThreads.length / CHATS_PER_PAGE);
    const safePageIndex = Math.min(chatHistoryPage, Math.max(totalPages - 1, 0));
    return sortedUnpinnedThreads.slice(
      safePageIndex * CHATS_PER_PAGE,
      safePageIndex * CHATS_PER_PAGE + CHATS_PER_PAGE
    );
  }, [sortedUnpinnedThreads, chatHistoryPage]);

  // Memoize thread title extraction to prevent re-computation
  const getThreadTitle = React.useCallback((thread: any) => {
    return (
      thread.thread_name?.value ||
      (thread.messages && thread.messages.length > 0
        ? thread.messages[thread.messages.length - 1].content.slice(0, 40)
        : "New Conversation")
    );
  }, []);

  // Memoize date formatting to prevent expensive toLocaleString calls
  const formatThreadDate = React.useCallback((seconds: number | string | undefined) => {
    if (!seconds) return "";
    try {
      return new Date(Number(seconds) * 1000).toLocaleString();
    } catch {
      return "";
    }
  }, []);

  // New Chat loading and snackbar
  const [newChatLoading, setNewChatLoading] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [pendingWorkflowName, setPendingWorkflowName] = useState<string | null>(null);
  const [workflowThreadCreated, setWorkflowThreadCreated] = useState(false);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  const [integrationSurface, setIntegrationSurface] = useState<{
    applications: string[];
    platforms: { name: string; type: string }[];
  } | null>(null);
  const [integrationSurfaceLoading, setIntegrationSurfaceLoading] = useState(false);
  const [hasProductivityApps, setHasProductivityApps] = useState<boolean | null>(null);

  const [initialWorkflowParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      workflow: params.get('workflow'),
      thread: params.get('thread')
    };
  });

  // Chat logic hook
  const {
    aiThreadId,
    messages: chatMessages,
    isLoading: chatIsLoading,
    sendMessage,
    stopTurn,
    queuedMessages,
    deleteQueuedMessage,
    updateQueuedMessage,
    sendQueuedMessageNow,
    canSendQueuedMessagesNow,
    connectionStatus,
  } = useUserChatHook(selectedUserThreadId, user?.email || "", {
    onSendError: showChatError,
  });

  // A live browser run is answered with the review controls, never with a chat message.
  const computerUseChatDisabledReason = useComputerUseChatDisabledReason(aiThreadId);
  const threadWait = useThreadWait(aiThreadId);
  const chatDisabledReason = composeChatDisabledReason(threadWait, computerUseChatDisabledReason);
  // Theo's open question takes a chat reply: the composer stays open and says so.
  const chatReplyPlaceholder = useChatReplyPlaceholder(aiThreadId);
  // The delete dialog here is opened from the thread list, so what it is about to remove is not
  // necessarily the thread on screen. Asked about that thread rather than this one, so the
  // warning names a check the technician is actually about to stop.
  const deletedThreadWait = useThreadWait(threadToDelete?.ai_thread_id ?? null);

  // One-shot URL attachment prefill for an already-created assistant thread.
  useEffect(() => {
    if (!routeThreadId) return;
    const params = new URLSearchParams(window.location.search);
    const key = [
      String(params.get(OPEN_REPORT_JOB_ID_QUERY_PARAM) || "").trim(),
      String(params.get(OPEN_REPORT_TITLE_QUERY_PARAM) || "").trim(),
      String(params.get(OPEN_REPORT_COMPLETED_AT_QUERY_PARAM) || "").trim(),
    ].join("|");
    if (!key || key === "||") return;
    if (consumedAttachmentParamRef.current.has(key)) return;
    consumedAttachmentParamRef.current.add(key);
    const jobId = Number(params.get(OPEN_REPORT_JOB_ID_QUERY_PARAM));
    const title = String(params.get(OPEN_REPORT_TITLE_QUERY_PARAM) || "").trim();
    if (!Number.isFinite(jobId) || jobId < 1 || !title) return;
    setPreloadForThreadId(String(routeThreadId));
    setPreloadedReportAttachments([
      {
        jobId,
        title,
        completedAt: String(params.get(OPEN_REPORT_COMPLETED_AT_QUERY_PARAM) || "").trim() || null,
      },
    ]);
  }, [routeThreadId]);

  const handlePreloadedReportAttachmentsConsumed = useCallback(() => {
    setPreloadedReportAttachments([]);
    setPreloadForThreadId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete(OPEN_REPORT_JOB_ID_QUERY_PARAM);
    params.delete(OPEN_REPORT_TITLE_QUERY_PARAM);
    params.delete(OPEN_REPORT_COMPLETED_AT_QUERY_PARAM);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const handleAttachCompletedResearchReport = useCallback(
    (report: ReportAttachmentTokenPayload) => {
      if (!selectedUserThreadId) return;
      const jobId = Number(report.jobId);
      if (!Number.isFinite(jobId) || jobId < 1) return;
      const title = String(report.title || `Report ${jobId}`).trim() || `Report ${jobId}`;
      setPreloadForThreadId(String(selectedUserThreadId));
      setPreloadedReportAttachments([
        {
          jobId,
          title,
          completedAt: report.completedAt ?? null,
          customerId: report.customerId ?? null,
        },
      ]);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [selectedUserThreadId]
  );

  // Keep rendered bubbles strictly scoped to the active assistant thread.
  const selectedScopedThread = React.useMemo(
    () =>
      (userThreads || []).find(
        (t: any) => String(t.ai_thread_id) === String(selectedUserThreadId)
      ),
    [userThreads, selectedUserThreadId]
  );

  const selectedThreadCustomerId = React.useMemo(
    () => normalizeThreadCustomerId(selectedScopedThread?.customer_id),
    [normalizeThreadCustomerId, selectedScopedThread?.customer_id]
  );

  const currentSurfaceMessages = React.useMemo(() => {
    return selectedScopedThread?.messages || [];
  }, [selectedScopedThread]);

  // Handle human review actions
  const handleReviewAction = async (
    action: "CONTINUE" | "CANCEL" | "UPDATE" | "SCHEDULED",
    args?: string,
    messageId?: string
  ) => {
    if (!aiThreadId || !user?.email) return;
    try {
      await dispatch(
        ChatAction.handleHumanReview(aiThreadId, user.email, action, args || "{}", messageId)
      );
    } catch (error) {
      dispatchChatSendFailure(dispatch, error);
      throw error;
    }
  };

  // Auto-scroll to bottom when messages change
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const [screenshotLightboxSrc, setScreenshotLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentSurfaceMessages]);

  const displayedMessages = React.useMemo(
    () => groupConsecutivePlanSteps(collapseStreamingChunks(currentSurfaceMessages)),
    [currentSurfaceMessages]
  );

  // Panels, seeding and placement all come from the shared hook so the assistant and a ticket
  // draw the same thing from the same state.
  const renderComputerUseRuns = useComputerUseRuns(selectedUserThreadId, displayedMessages);

  useEffect(() => {
    const root = messagesScrollRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const img = t.closest("img.neo-chat-inline-image") as HTMLImageElement | null;
      if (img?.src) {
        setScreenshotLightboxSrc(img.src);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [currentSurfaceMessages]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedUserThreadId) {
      setIntegrationSurface(null);
      setIntegrationSurfaceLoading(false);
      return;
    }
    setIntegrationSurfaceLoading(true);
    setIntegrationSurface(null);
    (async () => {
      try {
        const data: any = await chatApiInstance.getChatThreadIntegrationSurface(
          String(selectedUserThreadId)
        );
        if (!cancelled && data && typeof data === "object") {
          setIntegrationSurface({
            applications: Array.isArray(data.applications) ? data.applications : [],
            platforms: Array.isArray(data.platforms) ? data.platforms : [],
          });
        }
      } catch {
        if (!cancelled) setIntegrationSurface(null);
      } finally {
        if (!cancelled) setIntegrationSurfaceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedUserThreadId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedThreadCustomerId) {
      setHasProductivityApps(null);
      return;
    }
    setHasProductivityApps(null);
    (async () => {
      try {
        const response = await integrationApiInstance.getCustomerProductivityAppsStatus(
          String(selectedThreadCustomerId)
        );
        if (!cancelled) {
          setHasProductivityApps(response.has_productivity_apps);
        }
      } catch {
        if (!cancelled) setHasProductivityApps(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedThreadCustomerId]);

  const integrationPill = React.useMemo(() => {
    if (!integrationSurface) {
      return { short: "", tooltipLines: [] as string[], count: 0 };
    }
    const apps = integrationSurface.applications.map((a) => String(a || "").trim()).filter(Boolean);
    const platLabels = integrationSurface.platforms
      .map((p) => {
        const n = String(p.name || "").trim();
        const t = String(p.type || "").trim().toUpperCase();
        if (!n) return "";
        return t ? `${n} (${t})` : n;
      })
      .filter(Boolean);
    const all = [...apps, ...platLabels];
    const count = all.length;
    const short =
      count === 0
        ? ""
        : all.length <= 2
          ? all.join(" · ")
          : `${all.slice(0, 2).join(" · ")} +${all.length - 2}`;
    const tooltipLines: string[] = [];
    if (apps.length) tooltipLines.push(`Applications: ${apps.join(", ")}`);
    if (platLabels.length) tooltipLines.push(`Platforms: ${platLabels.join(", ")}`);
    return { short, tooltipLines, count };
  }, [integrationSurface]);

  // Fetch user threads only once per session
  const [threadsFetched, setThreadsFetched] = useState(false);

  const historyThreadId = routeThreadId || selectedUserThreadId;
  const {
    thread: historyThread,
    isLoading: threadDetailLoading,
    error: threadDetailError,
    seed: seedThreadHistory,
    retry: retryThreadHistory,
  } = useThreadHistory(historyThreadId, user?.email || "");
  const appliedHistory = useRef(new WeakSet<object>());

  useEffect(() => {
    setThreadsFetched(false);
  }, [isDeepResearchSurface]);

  useEffect(() => {
    if (user?.email && !threadsFetched) {
      dispatch(
        ChatAction.fetchUserThreads(user.email, {
          researchOnly: isDeepResearchSurface,
        })
      );
      setThreadsFetched(true);
    }
  }, [dispatch, isDeepResearchSurface, threadsFetched, user?.email]);

  // Select metadata immediately; messages arrive independently and are cached
  // for instant revisits. A late response for another route cannot select it.
  useEffect(() => {
    if (!routeThreadId) {
      setNotFoundThreadId(null);
      return;
    }
    const exists = (userThreads || []).some(
      (thread: any) => String(thread.ai_thread_id) === String(routeThreadId)
    );
    if (exists && String(selectedUserThreadId || "") !== String(routeThreadId)) {
      dispatch(ChatAction.setSelectedUserThreadId(routeThreadId));
    } else if (!exists && selectedUserThreadId && String(selectedUserThreadId) !== String(routeThreadId)) {
      dispatch({ type: ActionTypes.SET_SELECTED_USER_THREAD_ID, payload: null });
    }
  }, [routeThreadId, userThreads, selectedUserThreadId, dispatch]);

  useEffect(() => {
    if (threadDetailError) {
      if (threadDetailError.toLowerCase().includes("not found") && routeThreadId) {
        setNotFoundThreadId(routeThreadId);
      }
      return;
    }
    if (!historyThread) return;
    const invalid = historyThread?.ticket_id?.value || isDeviceAssistantThread(historyThread) ||
      (isDeepResearchSurface && !isResearchEnabledThread(historyThread));
    if (invalid) {
      if (routeThreadId) setNotFoundThreadId(routeThreadId);
      return;
    }
    if (!appliedHistory.current.has(historyThread)) {
      appliedHistory.current.add(historyThread);
      dispatch(ChatAction.upsertUserThread(historyThread));
    }
    if (String(selectedUserThreadId || "") !== String(historyThread.ai_thread_id)) {
      dispatch(ChatAction.setSelectedUserThreadId(String(historyThread.ai_thread_id)));
    }
    setNotFoundThreadId(null);
  }, [historyThread, threadDetailError, routeThreadId, selectedUserThreadId, dispatch, isDeepResearchSurface]);

  // Select the first thread by default if none selected (only when path doesn't specify thread id)
  useEffect(() => {
    if (routeThreadId) return;
    if ((userThreads || []).length === 0) {
      if (selectedUserThreadId !== null) {
        dispatch({ type: ActionTypes.SET_SELECTED_USER_THREAD_ID, payload: null });
      }
      return;
    }
    const selectedExists = selectedUserThreadId && (userThreads || []).some(
      (t: any) => String(t.ai_thread_id) === String(selectedUserThreadId)
    );
    const targetThreadId = selectedExists
      ? String(selectedUserThreadId)
      : String((userThreads || [])[0].ai_thread_id);

    if (String(selectedUserThreadId || "") !== targetThreadId) {
      dispatch(ChatAction.setSelectedUserThreadId(targetThreadId));
    }
    navigate(`${assistantBasePath}/${targetThreadId}`, { replace: true });
  }, [
    userThreads,
    selectedUserThreadId,
    routeThreadId,
    dispatch,
    navigate,
    assistantBasePath,
  ]);

  // DISABLED: fetch pills/prompts
  // useEffect(() => {
  //   dispatch(fetchPills(user?.customer_id || null));
  // }, [dispatch, user?.customer_id]);

  // Fetch customers on mount
  useEffect(() => {
    if (userMspInfo?.msp_id) {
      getCustomers();
    }
  }, [getCustomers, userMspInfo?.msp_id]);

  const allCustomers = React.useMemo(
    () => (customers?.rows || customers || []) as Customer[],
    [customers]
  );

  // Filter customers by MSP ID
  const filteredCustomers = React.useMemo(() => {
    return allCustomers.filter(
      (c: Customer) =>
        String((c as any).msp_id) === String(userMspInfo?.msp_id) &&
        c.name?.toLowerCase() !== "root" &&
        !isMspOrgCustomer(c)
    );
  }, [allCustomers, userMspInfo?.msp_id]);

  const newChatCustomerSelectOptions = React.useMemo(
    () => [
      { value: CHAT_NEW_THREAD_NO_CUSTOMER, label: "No Customer" },
      ...filteredCustomers.map((customer: Customer) => ({
        value: customer.id.toString(),
        label: customer.name,
      })),
    ],
    [filteredCustomers]
  );
  const customersAreLoadingForNewChat =
    Boolean(userMspInfo?.msp_id) &&
    (customersLoading || (!customersLoaded && !customersError));
  const customersFailedForNewChat =
    Boolean(userMspInfo?.msp_id) &&
    !customersLoading &&
    !customersLoaded &&
    Boolean(customersError);

  const openNewChatCustomerDialog = useCallback((workflowName?: string | null) => {
    setPendingWorkflowName(workflowName || null);
    setSelectedCustomerId(null);
    setShowCustomerDialog(true);
  }, []);

  // Prefer persisted DB title, then derive from customer for the brief window before rename completes.
  const chatHeaderTitle = React.useMemo(() => {
    const selectedThread = userThreads.find(
      (t: any) => String(t.ai_thread_id) === String(selectedUserThreadId)
    );
    const persisted = selectedThread?.thread_name?.value?.trim();
    if (persisted) {
      return persisted;
    }
    const threadCustomerId = normalizeThreadCustomerId(selectedThread?.customer_id);
    const customerId =
      threadCustomerId != null
        ? String(threadCustomerId)
        : selectedCustomerId === CHAT_NEW_THREAD_NO_CUSTOMER
          ? null
          : selectedCustomerId;
    const customer =
      customerId != null
        ? (filteredCustomers as Customer[]).find(
            (c: Customer) => String(c.id) === customerId
          )
        : undefined;
    return customer ? `New Chat with ${customer.name}` : "New Chat";
  }, [
    userThreads,
    selectedUserThreadId,
    selectedCustomerId,
    filteredCustomers,
    normalizeThreadCustomerId,
  ]);

  // Preload audit logs in background when a chat thread is open so Audit logs popup opens quickly.
  useEffect(() => {
    chatApiInstance.preloadToolAuditLogs(selectedUserThreadId);
  }, [selectedUserThreadId]);

  // Handle workflow auto-start from query parameter
  // Use initialWorkflowParams captured on mount instead of searchParams
  useEffect(() => {
    const workflowName = initialWorkflowParams.workflow;
    const threadId = initialWorkflowParams.thread || routeThreadId;
    
    // Only process if we have workflow parameter and haven't processed it yet
    if (workflowName && user?.email && !urlParamsProcessed) {
      
      // Mark as processed immediately to prevent re-processing
      setUrlParamsProcessed(true);
      
      // If thread ID is provided (coming from Workflows page), select it and send prompt
      if (threadId) {
        // Select the thread immediately
        dispatch(ChatAction.setSelectedUserThreadId(threadId));
        
        // Set workflow to send after thread is ready
        setPendingWorkflowName(workflowName);
        setWorkflowThreadCreated(true);
        
        // Refetch threads in the background to ensure list is up to date
        dispatch(
          ChatAction.fetchUserThreads(user.email, {
            researchOnly: isDeepResearchSurface,
          })
        );
        
        // Clear the URL parameters AFTER everything is set up
        setTimeout(() => {
          setSearchParams({}, { replace: true });
        }, 500);
      } else {
        // No thread ID, create a new thread after explicit customer selection.
        openNewChatCustomerDialog(workflowName);
        
        // Clear the URL parameters AFTER setting state
        setTimeout(() => {
          setSearchParams({}, { replace: true });
        }, 500);
      }
    }
  }, [
    dispatch,
    initialWorkflowParams,
    isDeepResearchSurface,
    openNewChatCustomerDialog,
    routeThreadId,
    setSearchParams,
    urlParamsProcessed,
    user?.email,
  ]);

  // Auto-send workflow prompt after thread is created and ready
  useEffect(() => {
    if (pendingWorkflowName && workflowThreadCreated && selectedUserThreadId && !chatIsLoading) {
      // Wait a bit for thread to be fully initialized, then send the workflow prompt
      const timer = setTimeout(() => {
        const workflowPrompt = `run: ${pendingWorkflowName}`;
        sendMessage(workflowPrompt, {
          assistantMode: canUseDeepResearch && isDeepResearchSurface
            ? AssistantMode.DEEP_RESEARCH
            : AssistantMode.CHAT,
        });
        setPendingWorkflowName(null);
        setWorkflowThreadCreated(false);
      }, 1000); // Increased delay to ensure thread is ready
      
      return () => clearTimeout(timer);
    }
  }, [
    canUseDeepResearch,
    chatIsLoading,
    isDeepResearchSurface,
    pendingWorkflowName,
    selectedUserThreadId,
    sendMessage,
    workflowThreadCreated,
  ]);

  // Sidebar handlers
  const handleSelectThread = (threadId: string) => {
    dispatch(ChatAction.setSelectedUserThreadId(threadId));
    navigate(`${assistantBasePath}/${threadId}`);
    setNotFoundThreadId(null);
    setInputMessage("");
    // setSelectedPill(null);
    // setShowPrompts(false);
    setSelectedCustomerId(null);
  };

  const handleNewChat = () => {
    openNewChatCustomerDialog();
  };

  const handleNewChatSameCustomer = () => {
    const customerId = selectedThreadCustomerId != null ? String(selectedThreadCustomerId) : null;
    const title = getInitialPersistedChatThreadTitle(customerId, filteredCustomers);
    void openInNewTab(
      async () => {
        const createdThreadId = await createNewChatThread(customerId, null, title, { keepCurrentChat: true });
        return createdThreadId ? `${assistantBasePath}/${createdThreadId}` : null;
      },
      () => createNewChatThread(customerId, null, title)
    );
  };

  async function createNewChatThread(
    customerId: string | null,
    workflowName?: string | null,
    initialThreadTitle: string = "New Chat",
    options?: {
      suppressSuccessSnackbar?: boolean;
      prefillMessage?: string | null;
      // The new chat opens elsewhere (a new tab): create it and list it, but leave this tab on its chat.
      keepCurrentChat?: boolean;
    }
  ): Promise<string | null> {
    if (!user?.email) return null;
    setNewChatLoading(true);
    setShowCustomerDialog(false);
    try {
      const response = await dispatch(
        ChatAction.createUserThread(
          user.email,
          customerId ? parseInt(customerId) : null,
          {
            researchEnabled: canUseDeepResearch && isDeepResearchSurface,
          }
        )
      );
      const createdThreadId =
        response?.ai_thread_id || response?.ai_thread?.ai_thread_id;

      if (!createdThreadId) return null;

      const seededUserId =
        response?.ai_thread?.user_id ?? response?.user_id ?? user.email;
      dispatch(
        ChatAction.upsertUserThread({
          ...(response?.ai_thread || {}),
          ai_thread_id: createdThreadId,
          user_id: seededUserId,
          shared: response?.ai_thread?.shared ?? false,
          research_enabled: (canUseDeepResearch && isDeepResearchSurface) || response?.research_enabled === true,
        })
      );
      const titleToPersist = (initialThreadTitle || "New Chat").trim() || "New Chat";
      try {
        await dispatch(
          ChatAction.renameUserThread(String(createdThreadId), titleToPersist)
        );
      } catch (renameErr) {
        console.error("[ChatAssistant] Failed to persist thread title:", renameErr);
      }
      if (options?.keepCurrentChat) return String(createdThreadId);
      setSelectedCustomerId(null);
      dispatch(ChatAction.setSelectedUserThreadId(createdThreadId));
      // Newly created threads are empty by definition: no detail fetch needed.
      const seededHistory = {
        ...(response?.ai_thread || {}),
        ai_thread_id: createdThreadId,
        messages: [],
        research_enabled: (canUseDeepResearch && isDeepResearchSurface) || response?.research_enabled === true,
      };
      appliedHistory.current.add(seededHistory);
      seedThreadHistory(String(createdThreadId), seededHistory);
      navigate(`${assistantBasePath}/${createdThreadId}`);
      setNotFoundThreadId(null);
      setChatHistoryPage(0);
      const prefill = String(options?.prefillMessage || "").trim();
      setInputMessage(prefill);
      // setSelectedPill(null);
      // setShowPrompts(false);

      // If this is a workflow auto-start, mark it and wait for thread to be ready
      if (workflowName) {
        setPendingWorkflowName(workflowName);
        setWorkflowThreadCreated(true);
      } else if (!options?.suppressSuccessSnackbar) {
        setSnackbarMsg(
          `New chat created successfully (ID: ${createdThreadId})`
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
      return String(createdThreadId);
    } finally {
      setNewChatLoading(false);
    }
  }

  // DISABLED: Pills/Prompts logic — re-enable with PillsBar + PromptsList + pills selectors/state above
  // const filteredPills: PillCategory[] = pillsKeyword
  //   ? pills.filter((pill: any) =>
  //       pill.label.toLowerCase().includes(pillsKeyword.toLowerCase())
  //     )
  //   : pills;
  // const selectedSuggestions = selectedPill
  //   ? pills.find((p: any) => p.id === selectedPill)?.suggestions ?? []
  //   : [];
  // const handlePillClick = (pillId: string) => {
  //   const pill = pills.find((p: any) => p.id === pillId);
  //   if (pill) {
  //     setInputMessage(pill.label);
  //     setSelectedPill(pill.id);
  //     setShowPrompts(true);
  //     setTimeout(() => {
  //       if (inputRef.current) inputRef.current.blur();
  //     }, 0);
  //   }
  // };
  // const handleSuggestionClick = (text: string) => {
  //   setInputMessage(text);
  //   setShowPrompts(false);
  //   setTimeout(() => {
  //     if (inputRef.current) inputRef.current.focus();
  //   }, 0);
  // };
  // const handleClosePrompts = () => {
  //   setSelectedPill(null);
  //   setShowPrompts(false);
  //   setTimeout(() => {
  //     if (inputRef.current) inputRef.current.focus();
  //   }, 0);
  // };

  // Typeahead handlers
  const handleInputChange = (newValue: string) => {
    setInputMessage(newValue);
    
    // Clear existing timer
    if (typeaheadDebounceTimer) {
      clearTimeout(typeaheadDebounceTimer);
    }
    
    // Set new timer to show typeahead after 250ms of no typing
    if (newValue.trim().length > 0) {
      const timer = setTimeout(() => {
        setShowTypeahead(true);
        setDebouncedInputText(newValue); // Set debounced input for API calls
      }, 250);
      setTypeaheadDebounceTimer(timer);
    } else {
      setShowTypeahead(false);
      setDebouncedInputText(''); // Clear debounced input
    }
  };

  const handleTypeaheadSelect = (selectedText: string) => {
    setInputMessage(selectedText);
    setShowTypeahead(false);
    // Focus the input after selection
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  const handleChoiceOptionClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const option = target?.closest?.("[data-neo-choice-option]") as HTMLElement | null;
    const choiceText = option?.getAttribute("data-neo-choice-option")?.trim();
    if (!choiceText) return;

    event.preventDefault();
    event.stopPropagation();
    setInputMessage(choiceText);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  const handleTypeaheadClose = () => {
    setShowTypeahead(false);
  };

  const handleTypeaheadNavigationChange = (hasNavigated: boolean) => {
    setHasNavigatedToTypeahead(hasNavigated);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (typeaheadDebounceTimer) {
        clearTimeout(typeaheadDebounceTimer);
      }
    };
  }, [typeaheadDebounceTimer]);

  const selectedThread = React.useMemo(
    () =>
      userThreads.find(
        (t: any) => String(t.ai_thread_id) === String(selectedUserThreadId)
      ) || null,
    [userThreads, selectedUserThreadId]
  );
  const selectedThreadOwnerEmail = normalizeEmail(selectedThread?.user_id);
  const isThreadShared = selectedThread?.shared === true;
  const canToggleThreadShare = Boolean(
    selectedThread &&
      currentUserEmail &&
      selectedThreadOwnerEmail &&
      selectedThreadOwnerEmail === currentUserEmail
  );
  const canDeleteSelectedThread = canToggleThreadShare;
  const showRouteNotFound = Boolean(
    routeThreadId && notFoundThreadId && String(routeThreadId) === String(notFoundThreadId)
  );

  const currentAssistantMode = canUseDeepResearch && isDeepResearchSurface
    ? AssistantMode.DEEP_RESEARCH
    : AssistantMode.CHAT;

  const handleToggleThreadShared = async () => {
    if (!selectedThread?.ai_thread_id || !canToggleThreadShare) return;
    const nextShared = !isThreadShared;
    setShareUpdating(true);
    try {
      await dispatch(
        ChatAction.updateThreadShared(String(selectedThread.ai_thread_id), nextShared)
      );
      setSnackbarMsg(nextShared ? "Thread shared with org" : "Org sharing disabled");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMsg("Failed to update org sharing");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setShareUpdating(false);
    }
  };

  const getDefaultFileLinearTaskTitle = useCallback(() => {
    const persisted = String(selectedThread?.thread_name?.value || "").trim();
    if (persisted) return persisted;
    const headerTitle = String(chatHeaderTitle || "").trim();
    if (headerTitle && headerTitle !== "New Chat") return headerTitle;
    return isDeepResearchSurface ? "Deep Research task" : "Theo chat task";
  }, [chatHeaderTitle, isDeepResearchSurface, selectedThread?.thread_name?.value]);

  const getDefaultFileLinearTaskThreadLink = useCallback(() => {
    if (typeof window !== "undefined") return window.location.href;
    return "";
  }, []);

  const handleOpenFileLinearTaskDialog = useCallback(() => {
    const fallbackAssignee = String(user?.email || "").trim();
    const fallbackPrefix = fallbackAssignee.split("@")[0]?.toLowerCase() || "";
    setFileLinearTaskTitle(getDefaultFileLinearTaskTitle());
    setFileLinearTaskDescription("");
    setFileLinearTaskThreadLink(getDefaultFileLinearTaskThreadLink());
    setFileLinearTaskTraceUrl("");
    setFileLinearTaskAssignee(fallbackAssignee);
    setFileLinearTaskAssigneesLoading(true);
    setFileLinearTaskTraceLoading(Boolean(selectedUserThreadId));
    setShowFileLinearTaskDialog(true);
    if (selectedUserThreadId) {
      chatApiInstance
        .getLinearTaskTraceUrl(String(selectedUserThreadId))
        .then((result) => {
          const traceUrl = String(result?.trace_url || "").trim();
          if (traceUrl) {
            setFileLinearTaskTraceUrl(traceUrl);
          }
        })
        .catch(() => {
          setFileLinearTaskTraceUrl("");
        })
        .finally(() => setFileLinearTaskTraceLoading(false));
    }
    chatApiInstance
      .getLinearTaskAssignees()
      .then((result) => {
        const list = Array.isArray(result?.assignees) ? result.assignees : [];
        setFileLinearTaskAssignees(list);
        if (list.length > 0) {
          const exactAssignee = list.find(
            (assignee) => assignee.email.toLowerCase() === fallbackAssignee.toLowerCase()
          );
          const prefixAssignee = fallbackPrefix
            ? list.find((assignee) => assignee.email.split("@")[0]?.toLowerCase() === fallbackPrefix)
            : undefined;
          setFileLinearTaskAssignee((exactAssignee || prefixAssignee || list[0]).email);
        }
      })
      .catch((error: any) => {
        setFileLinearTaskAssignees([]);
        setSnackbarMsg(error?.message || "Failed to load Linear assignees.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setFileLinearTaskAssigneesLoading(false));
  }, [
    getDefaultFileLinearTaskThreadLink,
    getDefaultFileLinearTaskTitle,
    selectedUserThreadId,
    user?.email,
  ]);

  const handleSubmitFileLinearTask = useCallback(async () => {
    if (!selectedUserThreadId || !fileLinearTaskTitle.trim()) return;
    setFileLinearTaskSubmitting(true);
    try {
      const result = await chatApiInstance.fileLinearTask(String(selectedUserThreadId), {
        title: fileLinearTaskTitle.trim(),
        description: fileLinearTaskDescription.trim(),
        ticket_link: fileLinearTaskThreadLink.trim(),
        assignee_email: fileLinearTaskAssignee.trim(),
        trace_url: fileLinearTaskTraceUrl.trim(),
      });
      const identifier = result.identifier || result.issue_id || "Linear task";
      setCreatedLinearTask({
        identifier,
        title: result.title || fileLinearTaskTitle.trim(),
        url: result.url || "",
      });
      setShowFileLinearTaskDialog(false);
    } catch (error: any) {
      setSnackbarMsg(error?.message || "Failed to create Linear task.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setFileLinearTaskSubmitting(false);
    }
  }, [
    fileLinearTaskAssignee,
    fileLinearTaskDescription,
    fileLinearTaskThreadLink,
    fileLinearTaskTitle,
    fileLinearTaskTraceUrl,
    selectedUserThreadId,
  ]);

  const fileLinearTaskAssigneeOptions = React.useMemo(() => {
    const options = [...fileLinearTaskAssignees];
    const fallbackEmail = String(user?.email || "").trim();
    if (fallbackEmail && !options.some((assignee) => assignee.email.toLowerCase() === fallbackEmail.toLowerCase())) {
      options.unshift({ id: "current-user", name: fallbackEmail, email: fallbackEmail });
    }
    return options;
  }, [fileLinearTaskAssignees, user?.email]);

  const fileLinearTaskMspLabel = React.useMemo(() => {
    const mspId = userMspInfo?.msp_id ? String(userMspInfo.msp_id) : "";
    const mspName = String(userMspInfo?.msp_name || "").trim();
    if (mspId && mspName) return `${mspName} (MSP ID: ${mspId})`;
    if (mspName) return mspName;
    if (mspId) return `MSP ID: ${mspId}`;
    return "";
  }, [userMspInfo?.msp_id, userMspInfo?.msp_name]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        if (width < MIN_WIDTH_FOR_EXPANDED_SIDEBAR && !sidebarCollapsed) {
          setSidebarCollapsed(true);
          setAutoCollapsed(true);
        } else if (width >= MIN_WIDTH_FOR_EXPANDED_SIDEBAR && autoCollapsed) {
          setSidebarCollapsed(false);
          setAutoCollapsed(false);
        }
      }
    };
    const observer = new window.ResizeObserver(handleResize);
    if (containerRef.current) {
      observer.observe(containerRef.current);
      handleResize();
    }
    return () => {
      observer.disconnect();
    };
  }, [sidebarCollapsed, autoCollapsed]);

  if (!user) return null;

  // If still loading user threads for the first time, show global overlay
  // if (user && userThreads.length === 0 && chatIsLoading) {
  //   return <GlobalLoadingOverlay />;
  // }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        height: "100vh - 40px",
        overflow: "hidden",
        margin: 2,
        borderRadius: 2,
        border: "1px solid #ECECEC",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          display: "flex",
          minHeight: "80vh",
          height: "100%",
          borderRadius: 3,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          background: "#fff",
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: sidebarCollapsed ? 60 : { xs: 80, sm: 240, md: 260 },
            transition: "width 0.2s",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid #ECECEC",
            minWidth: 0,
          }}
        >
          {/* Top Bar */}
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff",
              position: "relative",
            }}
          >
            {/* Logo container - always takes space even when hidden */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                visibility: sidebarCollapsed ? "hidden" : "visible",
                width: sidebarCollapsed ? 0 : "auto",
                overflow: "hidden",
                transition: "width 0.2s, visibility 0.2s",
              }}
            >
              <img
                src={theoLogo}
                alt="Neo Link"
                style={{
                  marginBottom: 4,
                  width: 48,
                  height: 48,
                }}
              />
            </Box>

            {/* Icon container - always positioned on the right */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                flexShrink: 0,
                position: "absolute",
                right: 16, // 16px from right edge (px: 2 = 16px)
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {sidebarCollapsed ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSidebarCollapsed(false);
                    setAutoCollapsed(false);
                  }}
                  onMouseEnter={() => setSidebarHover(true)}
                  onMouseLeave={() => setSidebarHover(false)}
                  sx={{
                    p: 0,
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={SidebarIcon}
                    alt={sidebarHover ? "Expand Sidebar" : "Sidebar Toggle"}
                    style={{ width: 24, height: 24 }}
                  />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  sx={{
                    p: 0,
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    setSidebarCollapsed(true);
                    setAutoCollapsed(false);
                  }}
                >
                  <img
                    src={SidebarIcon}
                    alt="Sidebar Toggle"
                    style={{ width: 24, height: 24 }}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
          {!sidebarCollapsed && (
            <>
              {/* Pinned Section */}
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    color: "#BAC0CC",
                    fontFamily: "Inter",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: 1.3,
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                    fontStyle: "normal",
                    px: 2,
                    mb: 1,
                  }}
                >
                  Pinned
                </Typography>
                {/* Render pinned chats here */}
                {sortedPinnedThreads.length === 0 && (
                  <Typography
                    sx={{ color: "#888", fontSize: 13, px: 2, mb: 1 }}
                  >
                    No pinned chats
                  </Typography>
                )}
                {sortedPinnedThreads.map((thread: any) => {
                      const title = getThreadTitle(thread);
                      return (
                          <Box
                            key={thread.ai_thread_id}
                            sx={{
                              borderRadius: 1,
                              mx: 1,
                              my: 0.5,
                              overflow: "hidden",
                              position: "relative",
                              "&:hover .chat-menu-btn": { opacity: 1 },
                              // Simple CSS transition instead of CSSTransition to avoid findDOMNode
                              transition: "opacity 0.2s ease-in-out",
                            }}
                          >
                            <Button
                              fullWidth
                              onClick={() =>
                                handleSelectThread(thread.ai_thread_id)
                              }
                              disableRipple
                              disableElevation
                              sx={{
                                justifyContent: "flex-start",
                                borderRadius: 1,
                                background:
                                  thread.ai_thread_id === selectedUserThreadId
                                    ? "rgba(102, 111, 141, 0.07)"
                                    : "transparent",
                                boxShadow: "none",
                                color: "#222",
                                "&:hover": {
                                  background: "rgba(102, 111, 141, 0.07)",
                                  boxShadow: "none",
                                },
                                "&:active": {
                                  background:
                                    thread.ai_thread_id === selectedUserThreadId
                                      ? "rgba(102, 111, 141, 0.12)"
                                      : "transparent",
                                  boxShadow: "none",
                                },
                                "&.Mui-focusVisible": {
                                  background:
                                    thread.ai_thread_id === selectedUserThreadId
                                      ? "rgba(102, 111, 141, 0.07)"
                                      : "transparent",
                                  boxShadow: "none",
                                },
                                transition:
                                  "background 0.2s, border-color 0.2s",
                                minHeight: 36,
                                px: 2,
                                textTransform: "none",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: "left",
                                }}
                              >
                                <Tooltip title={title} arrow>
                                  <Box
                                    sx={{
                                      fontWeight:
                                        thread.ai_thread_id ===
                                        selectedUserThreadId
                                          ? 500
                                          : 400,
                                      fontFamily: "var(--theo-font-sans)",
                                      color: "#666F8D",
                                      fontSize: 12,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: 180,
                                    }}
                                  >
                                    {title}
                                  </Box>
                                </Tooltip>
                                {thread.updated_at && (
                                  <Box
                                    sx={{
                                      fontSize: 10,
                                      color: "#888",
                                      mt: 0.5,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {formatThreadDate(thread.updated_at?.seconds)}
                                  </Box>
                                )}
                              </Box>
                              {/* Three-dot menu button, only visible on hover */}
                              <IconButton
                                component="span"
                                className="chat-menu-btn"
                                size="small"
                                disableRipple
                                disableFocusRipple
                                sx={{
                                  ml: 1,
                                  opacity: 0,
                                  transition: "opacity 0.2s",
                                  zIndex: 2,
                                  background: "transparent",
                                  boxShadow: "none",
                                  "&:hover": {
                                    background: "transparent",
                                    boxShadow: "none",
                                  },
                                  "&:active": {
                                    background: "transparent",
                                    boxShadow: "none",
                                  },
                                  "&.Mui-focusVisible": {
                                    background: "transparent",
                                    boxShadow: "none",
                                  },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuAnchorEl(e.currentTarget);
                                  setMenuThread(thread);
                                }}
                              >
                                <MoreHorizIcon fontSize="small" />
                              </IconButton>
                            </Button>
                          </Box>
                      );
                    })}
              </Box>
              {/* Chat History Section */}
              <Box
                sx={{
                  mt: 2,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    background: "#fff",
                    color: "#BAC0CC",
                    fontFamily: "Inter",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: 1.3,
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                    fontStyle: "normal",
                    px: 2,
                    mb: 1,
                    py: 1,
                  }}
                >
                  Chat History
                </Typography>
                <List
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 0,
                    background: "#fff",
                    minHeight: 0,
                    // Custom scrollbar
                    "::-webkit-scrollbar": {
                      width: "8px",
                      background: "#f5f5f5",
                    },
                    "::-webkit-scrollbar-thumb": {
                      background: "#e0e0e0",
                      borderRadius: "6px",
                    },
                    "::-webkit-scrollbar-thumb:hover": {
                      background: "#cccccc",
                    },
                    scrollbarColor: "#e0e0e0 #f5f5f5", // For Firefox
                    scrollbarWidth: "thin",
                  }}
                >
                  {sortedUnpinnedThreads.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary={
                          <span style={{ color: "#888" }}>No chats yet</span>
                        }
                      />
                    </ListItem>
                  )}
                  {paginatedUnpinnedThreads.map((thread: any) => {
                        const title = getThreadTitle(thread);
                        return (
                            <Box
                              key={thread.ai_thread_id}
                              sx={{
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                overflow: "hidden",
                                position: "relative",
                                "&:hover .chat-menu-btn": { opacity: 1 },
                                // Simple CSS transition instead of CSSTransition to avoid findDOMNode
                                transition: "opacity 0.2s ease-in-out",
                              }}
                            >
                              <Button
                                fullWidth
                                onClick={() =>
                                  handleSelectThread(thread.ai_thread_id)
                                }
                                disableRipple
                                disableElevation
                                sx={{
                                  justifyContent: "flex-start",
                                  borderRadius: 1,
                                  background:
                                    thread.ai_thread_id === selectedUserThreadId
                                      ? "rgba(102, 111, 141, 0.07)"
                                      : "transparent",
                                  boxShadow: "none",
                                  color: "#222",
                                  "&:hover": {
                                    background: "rgba(102, 111, 141, 0.07)",
                                    boxShadow: "none",
                                  },
                                  "&:active": {
                                    background:
                                      thread.ai_thread_id ===
                                      selectedUserThreadId
                                        ? "rgba(102, 111, 141, 0.12)"
                                        : "transparent",
                                    boxShadow: "none",
                                  },
                                  "&.Mui-focusVisible": {
                                    background:
                                      thread.ai_thread_id ===
                                      selectedUserThreadId
                                        ? "rgba(102, 111, 141, 0.07)"
                                        : "transparent",
                                    boxShadow: "none",
                                  },
                                  transition:
                                    "background 0.2s, border-color 0.2s",
                                  minHeight: 36,
                                  px: 2,
                                  textTransform: "none",
                                  position: "relative",
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: "left",
                                  }}
                                >
                                  <Tooltip title={title} arrow>
                                    <Box
                                      sx={{
                                        fontWeight:
                                          thread.ai_thread_id ===
                                          selectedUserThreadId
                                            ? 500
                                            : 400,
                                        fontFamily: "var(--theo-font-sans)",
                                        color: "#666F8D",
                                        fontSize: 12,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: 180,
                                      }}
                                    >
                                      {title}
                                    </Box>
                                  </Tooltip>
                                  {thread.updated_at && (
                                    <Box
                                      sx={{
                                        fontSize: 10,
                                        color: "#888",
                                        mt: 0.5,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {formatThreadDate(thread.updated_at?.seconds)}
                                    </Box>
                                  )}
                                </Box>
                                {/* Three-dot menu button, only visible on hover */}
                                <IconButton
                                  component="span"
                                  className="chat-menu-btn"
                                  size="small"
                                  disableRipple
                                  disableFocusRipple
                                  sx={{
                                    ml: 1,
                                    opacity: 0,
                                    transition: "opacity 0.2s",
                                    zIndex: 2,
                                    background: "transparent",
                                    boxShadow: "none",
                                    "&:hover": {
                                      background: "transparent",
                                      boxShadow: "none",
                                    },
                                    "&:active": {
                                      background: "transparent",
                                      boxShadow: "none",
                                    },
                                    "&.Mui-focusVisible": {
                                      background: "transparent",
                                      boxShadow: "none",
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuAnchorEl(e.currentTarget);
                                    setMenuThread(thread);
                                  }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton>
                              </Button>
                            </Box>
                        );
                      })}
                  {(() => {
                    const totalPages = Math.ceil(sortedUnpinnedThreads.length / CHATS_PER_PAGE);
                    if (totalPages <= 1) return null;
                    const safePageIndex = Math.min(chatHistoryPage, Math.max(totalPages - 1, 0));
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          py: 1,
                          px: 2,
                          borderTop: "1px solid #f0f0f0",
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled={safePageIndex === 0}
                          onClick={() => setChatHistoryPage((p) => Math.max(0, p - 1))}
                          sx={{
                            color: "#666F8D",
                            "&.Mui-disabled": { color: "#ccc" },
                          }}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#888",
                            fontFamily: "var(--theo-font-sans)",
                            userSelect: "none",
                          }}
                        >
                          {safePageIndex + 1} / {totalPages}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={safePageIndex >= totalPages - 1}
                          onClick={() => setChatHistoryPage((p) => Math.min(totalPages - 1, p + 1))}
                          sx={{
                            color: "#666F8D",
                            "&.Mui-disabled": { color: "#ccc" },
                          }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })()}
                </List>
              </Box>
            </>
          )}
          {/* Menu for chat actions */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={() => {
              setMenuAnchorEl(null);
            }}
            {...withOverlaySafeMenuProps({
              PaperProps: {
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                  minWidth: 130,
                  p: 0.5,
                  background: "#fff",
                },
              },
            })}
            MenuListProps={{
              disablePadding: true,
              sx: { p: 0 },
            }}
          >
            {/* Pin/Unpin menu item */}
            <MenuItem
              onClick={async () => {
                if (menuThread?.ai_thread_id) {
                  await dispatch(
                    ChatAction.updateThreadPinned(
                      menuThread.ai_thread_id,
                      !menuThread.pinned
                    )
                  );
                  setMenuAnchorEl(null);
                  setSnackbarMsg(
                    menuThread.pinned ? "Chat unpinned" : "Chat pinned"
                  );
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                }
              }}
              sx={{
                borderRadius: 2,
                px: 1,
                py: 0.7,
                fontSize: 14,
                color: "#222",
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 32,
                "&:hover": { background: "#F5F5F5" },
              }}
              disableRipple
            >
              <PushPinIcon sx={{ fontSize: 17, mr: 1 }} />
              {menuThread?.pinned ? "Unpin" : "Pin"}
            </MenuItem>
            <Divider sx={{ my: 0.3 }} />
            <MenuItem
              disabled={!menuThread?.thread_name?.value?.trim()}
              onClick={() => {
                if (!menuThread) return;
                setRenameDialogOpen(true);
                setThreadToRename(menuThread);
                setRenameValue(menuThread.thread_name?.value || "");
                setMenuAnchorEl(null);
              }}
              sx={{
                borderRadius: 2,
                px: 1,
                py: 0.7,
                fontSize: 14,
                color: "#222",
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 32,
                "&:hover": { background: "#F5F5F5" },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  color: "#999",
                },
              }}
              disableRipple
            >
              <EditOutlinedIcon sx={{ fontSize: 17, mr: 1 }} />
              Rename
            </MenuItem>
            <Divider sx={{ my: 0.3 }} />
            <MenuItem
              onClick={() => {
                if (!menuThread) return;
                setDeleteDialogOpen(true);
                setThreadToDelete(menuThread);
                setMenuAnchorEl(null);
              }}
              sx={{
                borderRadius: 2,
                px: 1,
                py: 0.7,
                fontSize: 14,
                color: "#E53935",
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 32,
                "&:hover": { background: "#FFF0F0" },
              }}
              disableRipple
            >
              <DeleteOutlineIcon
                sx={{ fontSize: 17, mr: 1, color: "#E53935" }}
              />
              Delete
            </MenuItem>
          </Menu>
          {/* Rename Dialog */}
          <Dialog
            open={renameDialogOpen}
            onClose={() => {
              setRenameDialogOpen(false);
              setThreadToRename(null);
            }}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                minWidth: 400,
                maxWidth: 500,
              },
            }}
          >
            <DialogTitle fontFamily="var(--theo-font-sans)" fontWeight="bold">
              Rename Chat
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Chat name"
                type="text"
                fullWidth
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                disabled={renameLoading}
                InputProps={{
                  sx: { fontFamily: "var(--theo-font-sans)" },
                }}
                InputLabelProps={{
                  sx: { fontFamily: "var(--theo-font-sans)" },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                sx={{ fontFamily: "var(--theo-font-sans)" }}
                onClick={() => {
                  setRenameDialogOpen(false);
                  setThreadToRename(null);
                }}
                disabled={renameLoading}
              >
                Cancel
              </Button>
              <Button
                sx={{ fontFamily: "var(--theo-font-sans)" }}
                onClick={async () => {
                  if (
                    !threadToRename ||
                    !renameValue.trim() ||
                    renameValue.trim() ===
                      (threadToRename.thread_name?.value || "").trim()
                  ) {
                    setRenameDialogOpen(false);
                    setThreadToRename(null);
                    return;
                  }
                  setRenameLoading(true);
                  try {
                    await dispatch(
                      ChatAction.renameUserThread(
                        threadToRename.ai_thread_id,
                        renameValue.trim()
                      )
                    );
                    setSnackbarMsg(
                      `Chat renamed successfully to "${renameValue.trim()}"`
                    );
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                    setRenameDialogOpen(false);
                    setThreadToRename(null);
                  } catch (error) {
                    setSnackbarMsg(`Failed to rename chat: ${error}`);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                  } finally {
                    setRenameLoading(false);
                  }
                }}
                disabled={!renameValue.trim() || renameLoading}
                variant="contained"
                startIcon={
                  renameLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {renameLoading ? "Renaming..." : "Rename"}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Delete Dialog */}
          <DeleteModal
            open={deleteDialogOpen}
            warning={deleteThreadWarning(null, deletedThreadWait)}
            onCancel={() => {
              setDeleteDialogOpen(false);
              setThreadToDelete(null);
            }}
            onDelete={async () => {
              if (threadToDelete) {
                const deletedId = threadToDelete.ai_thread_id;
                try {
                  await dispatch(ChatAction.deleteAiThread(deletedId));
                  setSnackbarMsg(
                    `Chat deleted successfully (ID: ${deletedId})`
                  );
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                  if (user?.email) {
                    const threads = await dispatch(
                      ChatAction.fetchUserThreads(user.email, {
                        researchOnly: isDeepResearchSurface,
                      })
                    );
                    // If the selected thread was deleted, pick a new one
                    if (selectedUserThreadId === deletedId) {
                      if (threads && threads.ai_threads.length > 0) {
                        const nextThreadId = String(
                          threads.ai_threads[0].ai_thread_id
                        );
                        dispatch(
                          ChatAction.setSelectedUserThreadId(
                            nextThreadId
                          )
                        );
                        navigate(`${assistantBasePath}/${nextThreadId}`, {
                          replace: true,
                        });
                      } else {
                        navigate(assistantBasePath, { replace: true });
                      }
                    }
                    // If the selected thread still exists, do nothing (keep selection)
                  }
                  setDeleteDialogOpen(false);
                  setThreadToDelete(null);
                } catch (error) {
                  setSnackbarMsg(`Failed to delete chat (ID: ${deletedId})`);
                  setSnackbarSeverity("error");
                  setSnackbarOpen(true);
                }
              }
            }}
            title="Delete Chat?"
            description="Are you sure you want to delete this chat? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
          />
          <ToolAuditLogPopup
            open={auditLogPopupOpen}
            onClose={() => setAuditLogPopupOpen(false)}
            aiThreadId={selectedUserThreadId}
            onLiveDebug={() => {
              setLiveDebug(true);
              setAuditLogPopupOpen(false);
            }}
          />
          {liveDebug && (
            <LiveDebugPanel
              aiThreadId={selectedUserThreadId}
              messages={chatMessages}
              isThinking={isThinking}
              onClose={() => setLiveDebug(false)}
            />
          )}
          <Dialog
            open={showFileLinearTaskDialog}
            onClose={() => {
              if (!fileLinearTaskSubmitting) setShowFileLinearTaskDialog(false);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}
          >
            <DialogTitle
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontWeight: 600,
                fontSize: 18,
                color: "#19213D",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AddTaskIcon sx={{ color: "#2563EB" }} />
                <span>File Linear Task</span>
              </Box>
              <IconButton
                size="small"
                disabled={fileLinearTaskSubmitting}
                onClick={() => setShowFileLinearTaskDialog(false)}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "32px !important", display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Title"
                value={fileLinearTaskTitle}
                onChange={(event) => setFileLinearTaskTitle(event.target.value)}
                fullWidth
                size="small"
                required
                autoFocus
                sx={{ mt: 0.5 }}
              />
              <TextField
                label="Description"
                value={fileLinearTaskDescription}
                onChange={(event) => setFileLinearTaskDescription(event.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={4}
                placeholder="Add context for the Linear task"
                helperText="Optional extra context to include in the Linear task description."
              />
              <TextField
                label="MSP"
                value={fileLinearTaskMspLabel || "MSP info unavailable"}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                helperText="This MSP info is included after the description in the Linear task."
              />
              <TextField
                label="Thread link"
                value={fileLinearTaskThreadLink}
                onChange={(event) => setFileLinearTaskThreadLink(event.target.value)}
                fullWidth
                size="small"
                helperText="This link is included separately in the Linear task."
              />
              <TextField
                label="Associated LangSmith trace link"
                value={fileLinearTaskTraceUrl}
                onChange={(event) => setFileLinearTaskTraceUrl(event.target.value)}
                fullWidth
                size="small"
                placeholder={fileLinearTaskTraceLoading ? "Loading latest trace..." : "Latest chat trace will be attached automatically if left blank"}
                InputProps={{
                  endAdornment: fileLinearTaskTraceLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ) : null,
                }}
                helperText={
                  fileLinearTaskTraceLoading
                    ? "Looking up the latest LangSmith trace for this thread..."
                    : "Leave blank to let Cortex attach the latest trace during task creation."
                }
              />
              <LayeredAutocompleteSelect
                label="Assigned to"
                value={fileLinearTaskAssignee}
                onChange={value => setFileLinearTaskAssignee(value)}
                fullWidth
                size="small"
                disabled={fileLinearTaskAssigneesLoading}
                helperText={
                  fileLinearTaskAssigneesLoading
                    ? "Loading Linear team members..."
                    : "Members are loaded from the configured Linear THEOP team."
                }
                disableClearable
                options={fileLinearTaskAssigneeOptions.map(assignee => ({ value: assignee.email, label: assignee.name + (assignee.email ? " (" + assignee.email + ")" : "") }))}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => setShowFileLinearTaskDialog(false)}
                disabled={fileLinearTaskSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitFileLinearTask}
                disabled={fileLinearTaskSubmitting || !fileLinearTaskTitle.trim() || !selectedUserThreadId}
                startIcon={fileLinearTaskSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  backgroundColor: "#2563EB",
                  "&:hover": { backgroundColor: "#1D4ED8" },
                }}
              >
                Create Task
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={Boolean(createdLinearTask)}
            onClose={() => setCreatedLinearTask(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontWeight: 600,
                fontSize: 18,
                color: "#19213D",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AddTaskIcon sx={{ color: "#2563EB" }} />
                <span>Linear Task Created</span>
              </Box>
              <IconButton
                aria-label="Close Linear task created dialog"
                size="small"
                onClick={() => setCreatedLinearTask(null)}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#19213D", mb: 1 }}>
                {createdLinearTask?.identifier || "Linear task"} was created successfully.
              </Typography>
              {createdLinearTask?.url ? (
                <MuiLink
                  href={createdLinearTask.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontFamily: "Inter", fontSize: 14, fontWeight: 600, color: "#2563EB" }}
                >
                  Open {createdLinearTask.identifier || createdLinearTask.title}
                </MuiLink>
              ) : null}
            </DialogContent>
          </Dialog>
          {selectedUserThreadId && (
            <UndoActionsModal
              open={undoModalOpen}
              onClose={() => { setUndoModalOpen(false); setUndoPreSelected([]); }}
              aiThreadId={selectedUserThreadId}
              userEmail={String(user?.email ?? "")}
              preSelectedCallIds={undoPreSelected}
            />
          )}
          {/* Snackbar (unified for delete and new chat) */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <MuiAlert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMsg}
            </MuiAlert>
          </Snackbar>
        </Box>
        {/* Main Chat Area - with title bar and content area */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              background: "#FFF",
              height: "100%",
              p: 0,
            }}
          >
            {/* Title Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: 64,
                px: 4,
                borderBottom: `1px solid ${NEUTRAL.border}`,
                background: "#fff",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    fontFamily: "var(--theo-font-sans)",
                    color: NEUTRAL.text,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {chatHeaderTitle}
                </Typography>
                {/* Connection: silent while connected, a quiet notice only when chat can't stream. */}
                <ConnectionNotice status={connectionStatus} />
                {selectedUserThreadId &&
                  (integrationSurfaceLoading || integrationPill.count > 0) && (
                    <Tooltip
                      title={
                        integrationPill.tooltipLines.length
                          ? integrationPill.tooltipLines.join("\n")
                          : integrationSurfaceLoading
                          ? "Loading integrations…"
                          : ""
                      }
                      placement="bottom"
                      enterDelay={400}
                    >
                      <Box
                        sx={{
                          maxWidth: 280,
                          background: NEUTRAL.surface,
                          border: `1px solid ${NEUTRAL.border}`,
                          color: NEUTRAL.textSecondary,
                          fontWeight: 500,
                          fontSize: "11px",
                          borderRadius: `${RADIUS.control}px`,
                          px: 0.75,
                          py: 0.25,
                          fontFamily: "var(--theo-font-sans)",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "default",
                        }}
                      >
                        {integrationSurfaceLoading ? (
                          <CircularProgress size={12} sx={{ color: NEUTRAL.textSecondary }} />
                        ) : (
                          <>
                            <HubOutlinedIcon sx={{ fontSize: 14, color: NEUTRAL.textSecondary, flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                              {integrationPill.short}
                            </span>
                          </>
                        )}
                      </Box>
                    </Tooltip>
                  )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleNewChat}
                  sx={{
                    borderRadius: `${RADIUS.control}px`,
                    textTransform: "none",
                    minWidth: 104,
                    minHeight: 32,
                    px: 1.5,
                    py: 0.5,
                    boxShadow: "none",
                    fontWeight: 600,
                    fontFamily: "var(--theo-font-sans)",
                    background: VIOLET.main,
                    "&:hover": { background: VIOLET.dark, boxShadow: "none" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  startIcon={
                    !newChatLoading ? <AddIcon sx={{ fontSize: 18 }} /> : null
                  }
                  disabled={newChatLoading}
                >
                  {newChatLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "New Chat"
                  )}
                </Button>
                {/* Grouped toolbar: one bordered segment, one quiet color (same as the ticket chat). */}
                <Box role="toolbar" aria-label="Chat actions" sx={{ ...TOOLBAR_GROUP_SX, ml: 1.5 }}>
                  {/* LangSmith: ROOT-only, first segment (chat-assistant / deep research). */}
                  {canOpenLangSmith ? (
                    <LangSmithThreadLinkButton aiThreadId={selectedUserThreadId} variant="toolbar" />
                  ) : null}
                  {isRootUser && (
                    <Tooltip title={!selectedUserThreadId ? "Open a thread first" : "New chat with same customer"}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label="New chat with same customer"
                          onClick={handleNewChatSameCustomer}
                          disabled={!selectedUserThreadId || newChatLoading}
                          sx={toolbarButtonSx(false)}
                        >
                          <ControlPointDuplicateOutlinedIcon sx={{ fontSize: toolbarIconSize(false) }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canFileLinearTask && (
                    <Tooltip title={!selectedUserThreadId ? "Open a thread first" : "File Linear task"}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label="File Linear task"
                          onClick={handleOpenFileLinearTaskDialog}
                          disabled={!selectedUserThreadId}
                          sx={toolbarButtonSx(false)}
                        >
                          <AddTaskOutlinedIcon sx={{ fontSize: toolbarIconSize(false) }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      !selectedUserThreadId
                        ? "Open a thread first"
                        : !canToggleThreadShare
                        ? "Only thread owner can change sharing"
                        : isThreadShared
                        ? "Disable org sharing"
                        : "Share with org"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        aria-label={isThreadShared ? "Disable org sharing" : "Share with org"}
                        aria-pressed={isThreadShared}
                        onClick={handleToggleThreadShared}
                        disabled={!selectedUserThreadId || !canToggleThreadShare || shareUpdating}
                        sx={{
                          ...(toolbarButtonSx(false) as object),
                          ...(isThreadShared ? { color: VIOLET.main, backgroundColor: VIOLET.wash } : {}),
                        }}
                      >
                        <DomainOutlinedIcon sx={{ fontSize: toolbarIconSize(false) }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Audit logs">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Audit logs"
                        onClick={() => setAuditLogPopupOpen(true)}
                        disabled={!selectedUserThreadId}
                        sx={toolbarButtonSx(false)}
                      >
                        <ShieldOutlinedIcon sx={{ fontSize: toolbarIconSize(false) }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <ClearChatButton
                    disabled={!selectedUserThreadId || !canDeleteSelectedThread}
                    tooltip={
                      !selectedUserThreadId
                        ? "Open a thread first"
                        : !canDeleteSelectedThread
                        ? "Only thread owner can delete"
                        : "Clear chat"
                    }
                    onClearChat={() => {
                      const thread = userThreads.find(
                        (t: any) => t.ai_thread_id === selectedUserThreadId
                      );
                      setDeleteDialogOpen(true);
                      setThreadToDelete(thread);
                    }}
                  />
                </Box>
              </Box>
            </Box>
            {/* Content Area */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                minHeight: 0,
                px: { xs: 2, md: 4 },
                mt: 2,
                background: "#fff",
              }}
            >
              <ProductivityAppsWarningBanner hasProductivityApps={hasProductivityApps} />
              {/* Messages */}
              <Box
                ref={messagesScrollRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  fontFamily: "var(--theo-font-sans)",
                  mb: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  scrollbarWidth: "none",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {showRouteNotFound ? (
                  <Box sx={{ mt: 2 }}>
                    <ChatBubble
                      sender="assistant"
                      content={<div>Thread not found</div>}
                      timestamp={formatTimestamp(String(Math.floor(Date.now() / 1000)))}
                      senderName="Theo Assistant"
                      userId="Theo Assistant"
                      isHistory
                    />
                  </Box>
                ) : currentSurfaceMessages.length === 0 ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 200,
                    }}
                  >
                    {threadDetailLoading ? (
                      <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <CircularProgress size={24} aria-label="Loading messages" />
                        <Typography color="text.secondary">Loading messages…</Typography>
                      </Box>
                    ) : threadDetailError ? (
                      <Alert severity="error" action={<Button color="inherit" onClick={retryThreadHistory}>Retry</Button>}>
                        Unable to load messages. Please try again.
                      </Alert>
                    ) : null}
                  </Box>
                ) : (
                  <>
                    {renderComputerUseRuns(BEFORE_FIRST_MESSAGE)}
                    {displayedMessages.map((msg: any, index: number, array: any[]) => (
                      (() => {
                        const bubblePres = getChatBubblePresentation(
                          msg.user_id,
                          user?.email ?? null,
                          user ?? null,
                          true,
                          agentDisplayNameForMessage(displayedMessages, index),
                        );
                        const isNeo = isNeoAssistantAuthor(msg.user_id);
                        const analyticsJobId =
                          isNeo
                            ? extractResearchJobId(msg.content)
                            : null;
                        const reportAttachments =
                          !isNeo
                            ? parseReportAttachmentsFromContent(msg.content)
                            : [];
                        const displayMessageContent =
                          !isNeo
                            ? stripReportAttachmentTokensForDisplay(msg.content)
                            : String(msg.content || "");
                        const bubbleKey = msg.entry_id
                          ? `entry-${msg.entry_id}`
                          : msg.isStreaming
                          ? `msg-${msg.message_id || "stream"}-${index}-${msg.created_at?.seconds || ""}-${msg.created_at?.nanos || 0}-${(msg.content || "").length}`
                          : `msg-${msg.message_id || "final"}-${index}-${msg.created_at?.seconds || Date.now()}-${msg.created_at?.nanos || 0}`;
                        return (
                        <React.Fragment key={bubbleKey}>
                        <ChatBubble
                          sender={bubblePres.chatBubbleSender}
                          content={
                            <div>
                              <div
                                onClick={handleChoiceOptionClick}
                                dangerouslySetInnerHTML={{
                                  __html: cleanContent(displayMessageContent),
                                }}
                              />
                              {canUseDeepResearch && analyticsJobId ? (
                                <ResearchReportJobStatusInline
                                  jobId={analyticsJobId}
                                  aiThreadId={selectedUserThreadId}
                                  pollEnabled={true}
                                  onAttachReport={handleAttachCompletedResearchReport}
                                />
                              ) : null}
                            </div>
                          }
                          timestamp={
                            formatTimestamp(msg.created_at?.seconds)
                          }
                          senderName={bubblePres.senderName}
                          userAvatar={bubblePres.userAvatar}
                          userId={bubblePres.userIdLabel}
                          human_review={msg.human_review}
                          human_review_status={msg.human_review_status}
                          isHistory={msg.isHistory}
                          isLastMessage={index === array.length - 1}
                          onReviewAction={handleReviewAction}
                          onReviewError={showChatError}
                          messageId={msg.message_id}
                          message={msg as any}
                          currentUser={user}
                          aiThreadId={aiThreadId || selectedUserThreadId || undefined}
                          customerId={selectedThreadCustomerId}
                          headerInlineContent={
                            reportAttachments.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {reportAttachments.map((attachment) => (
                                  <MuiLink
                                    key={`${msg.message_id || "msg"}-report-header-${attachment.jobId}`}
                                    href={`/report/${encodeURIComponent(String(attachment.jobId))}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    underline="none"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      border: "1px solid #D1D5DB",
                                      backgroundColor: "#F9FAFB",
                                      color: "#374151",
                                      borderRadius: "999px",
                                      px: 1,
                                      py: 0.2,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      fontFamily: "var(--theo-font-sans)",
                                      lineHeight: 1.2,
                                      maxWidth: 420,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      "&:hover": {
                                        backgroundColor: "#F3F4F6",
                                      },
                                    }}
                                  >
                                    {`report:${attachment.title}`}
                                  </MuiLink>
                                ))}
                              </Box>
                            ) : null
                          }
                          onOpenUndoModal={openUndoModalWithPreSelection}
                        />
                        {/* A browser run renders where it happened: after the message that
                            asked for it, before the reply that reports what it did. */}
                        {renderComputerUseRuns(index)}
                        </React.Fragment>
                        );
                      })()
                    ))}
                    {(Boolean(isThinking || chatIsLoading) && !newChatLoading && !threadWait?.stop_turn) && (
                      <Box
                        sx={{ display: "flex", justifyContent: "left", ml: 4 }}
                      >
                        {isThinking ? (
                          <ThinkingIndicator
                            thinkingSteps={thinkingSteps}
                            currentStep={currentThinkingStep || 0}
                          />
                        ) : (
                          <TypingIndicator />
                        )}
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>
              {/* DISABLED: PillsBar + PromptsList — re-enable with pills logic above
              <Box sx={{ mb: 1 }}>
                <PillsBar
                  selectedPill={selectedPill}
                  onSelect={handlePillClick}
                  pillCategories={filteredPills}
                />
              </Box>
              {showPrompts &&
                selectedPill &&
                selectedSuggestions.length > 0 && (
                  <ClickAwayListener onClickAway={handleClosePrompts}>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 72,
                        left: 0,
                        right: 0,
                        px: 4,
                        zIndex: 20,
                      }}
                    >
                      <PromptsList
                        suggestions={selectedSuggestions}
                        onSelect={handleSuggestionClick}
                        onClose={handleClosePrompts}
                        inputRef={inputRef}
                      />
                    </Box>
                  </ClickAwayListener>
                )}
              */}
              {/* InputBox always at the bottom */}
              <Box sx={{ mt: "auto", pb: 2, position: "relative" }}>
                {/* Typeahead positioned above InputBox */}
                <ClickAwayListener onClickAway={handleTypeaheadClose}>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "100%",
                      left: 0,
                      width: "100%",
                      zIndex: 25,
                    }}
                  >
                    <Typeahead
                      inputText={inputMessage}
                      debouncedInputText={debouncedInputText}
                      onSelect={handleTypeaheadSelect}
                      onClose={handleTypeaheadClose}
                      visible={showTypeahead}
                      customerId={null}
                      mspId={userMspInfo?.msp_id?.toString()}
                      onNavigationChange={handleTypeaheadNavigationChange}
                    />
                  </Box>
                </ClickAwayListener>
                <RemoteSessionInputSection
                  inputMessage={inputMessage}
                  setInputMessage={handleInputChange}
                  chatIsLoading={Boolean(isThinking || chatIsLoading)}
                  aiThreadId={aiThreadId}
                  disabledReason={chatDisabledReason}
                  placeholder={chatReplyPlaceholder}
                  preloadedReportAttachments={preloadedReportAttachments}
                  preloadForThreadId={preloadForThreadId}
                  onPreloadedReportAttachmentsConsumed={handlePreloadedReportAttachmentsConsumed}
                  currentMspId={userMspInfo?.msp_id ?? null}
                  enableReportAttachments={canUseDeepResearch}
                  userId={user?.email ?? null}
                  queuedMessages={queuedMessages}
                  onDeleteQueuedMessage={deleteQueuedMessage}
                  onUpdateQueuedMessage={updateQueuedMessage}
                  onSendQueuedMessageNow={sendQueuedMessageNow}
                  canSendQueuedMessagesNow={canSendQueuedMessagesNow}
                  handleSendMessage={(messageOverride?: string | ChatSendPayload) => {
                    const payload =
                      typeof messageOverride === "object" && messageOverride !== null
                        ? messageOverride
                        : { content: String(messageOverride ?? inputMessage) };
                    const textToSend = String(payload.content || "").trim();
                    const attachments = Array.isArray(payload.attachments) ? payload.attachments : [];
                    if (!textToSend && attachments.length === 0) return;
                    sendMessage(textToSend, {
                      assistantMode: currentAssistantMode,
                      attachments,
                    });
                    setInputMessage("");
                    // setSelectedPill(null);
                    // setShowPrompts(false);
                    setShowTypeahead(false);
                  }}
                  onStopTurn={stopTurn}
                  inputRef={inputRef}
                  showTypeahead={showTypeahead}
                  hasNavigatedToTypeahead={hasNavigatedToTypeahead}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Customer Selection Dialog */}
      <Dialog
        open={showCustomerDialog}
        onClose={() => setShowCustomerDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={getDropdownSafeDialogSx({
          borderRadius: 3,
          minWidth: 400,
          maxWidth: 500,
        })}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: 18,
            color: "#222",
            pb: 1,
          }}
        >
          Select Customer
        </DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 14,
              color: "#666F8D",
              mb: 2,
            }}
          >
            Select a customer for this chat, or choose &quot;No Customer&quot; for a general thread.
          </Typography>
          {customersFailedForNewChat ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => getCustomers()}
                >
                  Retry
                </Button>
              }
              sx={{ mb: 2 }}
            >
              Could not load customers. Try again before creating a new chat.
            </Alert>
          ) : null}
          <LayeredAutocompleteSelect
            label="Customer"
            required
            disableClearable
            value={selectedCustomerId || ""}
            onChange={(nextValue) => setSelectedCustomerId(nextValue ? nextValue : null)}
            options={newChatCustomerSelectOptions}
            disabled={customersAreLoadingForNewChat || customersFailedForNewChat}
            loading={customersAreLoadingForNewChat}
            noOptionsText="No customers found"
            helperText={
              customersAreLoadingForNewChat
                ? "Loading customers..."
                : filteredCustomers.length === 0
                  ? "No configured customers."
                  : undefined
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowCustomerDialog(false)}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              color: "#666F8D",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              createNewChatThread(
                resolveNewChatCustomerIdForApi(selectedCustomerId),
                pendingWorkflowName || undefined,
                isDeepResearchSurface
                  ? "Deep Research"
                  : getInitialPersistedChatThreadTitle(selectedCustomerId, filteredCustomers)
              )
            }
            variant="contained"
            disabled={
              newChatLoading ||
              customersAreLoadingForNewChat ||
              customersFailedForNewChat ||
              selectedCustomerId == null ||
              selectedCustomerId === ""
            }
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              background: "#705D48",
              "&:hover": { background: "#5a4b3a" },
            }}
          >
            {newChatLoading ? "Creating..." : "Create Chat"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(screenshotLightboxSrc)}
        onClose={() => setScreenshotLightboxSrc(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ p: 1, position: "relative" }}>
          <IconButton
            aria-label="Close screenshot"
            onClick={() => setScreenshotLightboxSrc(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "background.paper",
            }}
          >
            <CloseIcon />
          </IconButton>
          {screenshotLightboxSrc ? (
            <Box
              component="img"
              src={screenshotLightboxSrc}
              alt="Enlarged desktop screenshot"
              sx={{ width: "100%", height: "auto", display: "block", maxHeight: "90vh", objectFit: "contain" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatAssistant;
